import { useLocation } from "react-router-dom";

import GridTestProductCard from "@/components/GridTestProductCard";
import { TestProductModalProvider } from "@/contexts/TestProductModalContext";
import { TestProductOptionProvider } from "@/contexts/TestProductOptionContext";
import {
  CONSUMER_TEST_PRODUCT_DETAIL_QUERY,
  CONSUMER_TEST_PRODUCT_OPTION_FIELDS,
  CONSUMER_TEST_PRODUCT_WITH_OPTIONS_CONNECTIONS_QUERY,
  SHOP_LISTING_FILTERS_QUERY
} from "@/graphql/shop";
import { useDebounce } from "@/hooks/useDebounce";
import useListControls from "@/hooks/useListControls";
import TestProductModal from "@/tpo/shop/TestProductModal";
import useProductFilters from "@/tpo/shop/hooks/useProductFilters";
import useTestProducts from "@/tpo/shop/hooks/useTestProducts";
import { useQuery } from "@apollo/client";

import ExpertsListPage, { PractitionerFavouriteToolbarFooter } from "./ExpertsListPage";
import ListTemplate from "./ListTemplate";
import buildFilterSidebar from "./buildFilterSidebar";

function SupportedTests() {
  const { data: { sampleTypes = [], categories = [], testingServices = [] } = {} } = useQuery(
    SHOP_LISTING_FILTERS_QUERY
  );

  const location = useLocation();

  const { allFilters, validatedFilters, toggleFilter, resetFilters } = useProductFilters({
    defaultFilters: {
      gender: "all",
      categories: [],
      sampleTypes: [],
      testingServices: []
    },
    allFilters: {
      categories: [...categories.map(cat => cat.name)].sort(),
      gender: ["all", "female", "male"],
      sampleTypes: [...sampleTypes].sort(),
      testingServices: [...testingServices].sort()
    },
    filterSelected: {
      gender: ["male", "female"]
    }
  });

  const controls = useListControls({
    sortOptions: [
      {
        label: "Recommended",
        value: "-product_audience,sort_order"
      },
      {
        label: "A - Z",
        value: "-product_audience,name"
      },
      {
        label: "Z - A",
        value: "-product_audience,-name"
      },
      {
        label: "Price Asc",
        value: "-product_audience,rrp_current_price"
      },
      {
        label: "Price Desc",
        value: "-product_audience,-rrp_current_price"
      }
    ]
  });

  const search = new URLSearchParams(location.search).get("search") || "";

  const debouncedSearch = useDebounce(search, 200);

  const includePractitionerSupportedTests = true;

  const {
    products: { items: products, hasMore, endCursor },
    loading,
    fetchMore
  } = useTestProducts({
    query: CONSUMER_TEST_PRODUCT_WITH_OPTIONS_CONNECTIONS_QUERY,
    connectionsFieldName: "consumerTestProductConnections",
    search: debouncedSearch,
    sort: controls.sort,
    validatedFilters,
    queryVariables: {
      productAudience: includePractitionerSupportedTests ? undefined : "B2C"
    }
  });

  const filterSidebar = buildFilterSidebar({
    allFilters,
    validatedFilters,
    toggleFilter,
    resetFilters
  });

  return (
    <ExpertsListPage
      header="Practitioner supported tests"
      subheader="Unlock the full list of extensive functional testing to cover your every need"
      filterSidebar={filterSidebar}
      search={search}
      allFilters={allFilters}
      validatedFilters={validatedFilters}
      toggleFilter={toggleFilter}
      resetFilters={resetFilters}
      controls={controls}
      toolbarFooter={<PractitionerFavouriteToolbarFooter />}
    >
      <ListTemplate.Grid
        ItemCard={GridTestProductCard}
        fetchMore={fetchMore}
        hasMore={hasMore}
        endCursor={endCursor}
        loading={loading}
        items={products}
      />
    </ExpertsListPage>
  );
}

export default function SupportedTestsRoute() {
  return (
    <TestProductOptionProvider
      testProductOptionFragment={CONSUMER_TEST_PRODUCT_OPTION_FIELDS}
      testProductOptionFragmentName="ConsumerTestProductOptionFields"
      testProductOptionFragmentType="TestProductOptionType"
    >
      <TestProductModalProvider
        testProductQuery={CONSUMER_TEST_PRODUCT_DETAIL_QUERY}
        testProductFieldName="consumerTestProduct"
      >
        <SupportedTests />
        <TestProductModal bg="white" />
      </TestProductModalProvider>
    </TestProductOptionProvider>
  );
}
